import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

const ProjectMilestone = () => {
  const pid = useSelector((state) => state.user.projectId);
  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState('');
  const [milestoneGroups, setMilestoneGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!pid) return;
    
    const fetchStates = async () => {
      try {
        const response = await fetch(
          `https://api-project360-insights.haqdarshak.com/milestone/unique-states/?pid=${encodeURIComponent(pid)}`
        );
        if (!response.ok) throw new Error('Failed to fetch states');
        const data = await response.json();
        setStates(data.states || []);
      } catch (err) {
        setError(`Error loading states: ${err.message}`);
      }
    };

    fetchStates();
  }, [pid]);

  // Fetch milestone data
  useEffect(() => {
    if (!pid) return;

    const fetchMilestoneData = async () => {
      setLoading(true);
      setError(null);
      try {
        let url = `https://api-project360-insights.haqdarshak.com/milestone/project-milestone-achieved/?pid=${encodeURIComponent(pid)}`;
        if (selectedState) {
          url += `&state=${encodeURIComponent(selectedState)}`;
        }

        const response = await fetch(url);
        if (!response.ok) throw new Error('Failed to fetch data');
        const data = await response.json();
        
        // Validate and transform data
        if (!data.weeks) {
          throw new Error('No weeks data found in response');
        }

        const groups = {};
        data.weeks.forEach(week => {
          if (!week.milestone_name) return;
          
          if (!groups[week.milestone_name]) {
            groups[week.milestone_name] = {
              milestone_name: week.milestone_name,
              expected_completion_date: week.expected_milestone_end_date || '',
              weeks: [],
              status: week.milestone_status || 'To Do'
            };
          }
          
          groups[week.milestone_name].weeks.push({
            week_nos: `Week ${week.week_in_milestone || 1}(${week.week_start})${week.is_milestone_end ? ' (End)' : ''}`,
            is_milestone_end: week.is_milestone_end || false,
            metrics: {
              applications: week.targets?.applications,
              benefits: week.targets?.benefits,
              dfl: week.targets?.dfl,
              active_hds: week.targets?.active_hds
            },
            achieved: {
              screened: week.achieved?.ScreenedCitizens,
              brScreened: week.achieved?.BRScreenCitizens,
              dflAchieved: week.achieved?.DFLAchieved
            }
          });
        });

        setMilestoneGroups(Object.values(groups));
      } catch (err) {
        setError(`No week wise data is available for this project.
If you would like further information, please send a data request to: data@haqdarshak.com`);
        setMilestoneGroups([]);
      } finally {
        setLoading(false);
      }
    };

    fetchMilestoneData();
  }, [pid, selectedState]);

  // Helper function to format values (show 0 only for milestone end rows)
  const formatValue = (value, isMilestoneEnd) => {
    if (value === 0 && !isMilestoneEnd) return '';
    return value?.toLocaleString() || '';
  };

  return (
    <div className="container mx-auto p-4 font-sans">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <h1 className="text-xl md:text-xl font-bold text-gray-800">Week Wise Achievement</h1>
        <div className="w-full md:w-64">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Filter by State:
          </label>
          <select
            value={selectedState}
            onChange={(e) => setSelectedState(e.target.value)}
            className="w-full rounded-md border border-gray-300 bg-white py-2 px-3 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 text-sm transition-colors"
          >
            <option value="">All States</option>
            {states.map((state) => (
              <option key={state} value={state}>{state}</option>
            ))}
          </select>
        </div>
      </div>

      {loading && (
        <div className="flex justify-center items-center p-8">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Loading milestone data...</span>
        </div>
      )}
      
      {error && (
        <div className="p-4 mb-6 rounded-md bg-red-50 border-l-4 border-r-4 border-red-500">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              {/* <svg className="h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg> */}
            </div>
            <div className="ml-3">
              <p className="text-md font-medium text-green-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {milestoneGroups.length > 0 ? (
        <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm mb-8">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gradient-to-r from-blue-600 to-blue-800">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider border-r border-blue-500">Milestone Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider border-r border-blue-500">Expected Completion</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider border-r border-blue-500">Week No</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider border-r border-blue-500">
                Schemes DS (Planned)
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider border-r border-blue-500">
                Schemes DS (Achieved)
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider border-r border-blue-500">
                Schemes BR (Planned)
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider border-r border-blue-500">
                Schemes BR (Achieved)
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider border-r border-blue-500">
                DFL BR(Planned)
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider border-r border-blue-500">
                DFL BR (Achieved)
              </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider border-r border-blue-500">Active HDs (Planned)</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {milestoneGroups.map((group, gIndex) => (
                <React.Fragment key={gIndex}>
                  {group.weeks.map((week, wIndex) => (
                    <tr 
                      key={`${gIndex}-${wIndex}`} 
                      className={wIndex % 2 === 0 ? 'bg-white hover:bg-gray-50' : 'bg-gray-50 hover:bg-gray-100'}
                    >
                      {wIndex === 0 && (
                        <>
                          <td 
                            rowSpan={group.weeks.length} 
                            className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-gray-900 border-r border-gray-200 align-top"
                          >
                            <div className="font-medium text-blue-800">{group.milestone_name}</div>
                          </td>
                          <td 
                            rowSpan={group.weeks.length} 
                            className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 border-r border-gray-200 align-top"
                          >
                            {group.expected_completion_date || 'N/A'}
                          </td>
                        </>
                      )}
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 border-r border-gray-200">
                        {week.week_nos}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 border-r border-gray-200">
                        {formatValue(week.metrics.applications, week.is_milestone_end)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 border-r border-gray-200">
                        <span className={week.achieved?.screened >= (week.metrics.applications || 0) ? 'text-green-600 font-medium' : 'text-orange-600'}>
                          {formatValue(week.achieved?.screened, week.is_milestone_end)}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 border-r border-gray-200">
                        {formatValue(week.metrics.benefits, week.is_milestone_end)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 border-r border-gray-200">
                        <span className={week.achieved?.brScreened >= (week.metrics.benefits || 0) ? 'text-green-600 font-medium' : 'text-orange-600'}>
                          {formatValue(week.achieved?.brScreened, week.is_milestone_end)}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 border-r border-gray-200">
                        {formatValue(week.metrics.dfl, week.is_milestone_end)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 border-r border-gray-200">
                        <span className={week.achieved?.dflAchieved >= (week.metrics.dfl || '') ? 'text-green-600 font-medium' : 'text-orange-600'}>
                          {formatValue(week.achieved?.dflAchieved, week.is_milestone_end)}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 border-r border-gray-200">
                        {formatValue(week.metrics.active_hds, week.is_milestone_end)}
                      </td>
                      {wIndex === 0 && (
                        <td 
                          rowSpan={group.weeks.length} 
                          className="px-4 py-3 whitespace-nowrap border-l border-gray-200 align-top"
                        >
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                            group.status === 'Completed' ? 'bg-green-100 text-green-800' :
                            group.status === 'In Progress' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {group.status}
                          </span>
                        </td>
                      )}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        !loading && (
          <div className="p-8 text-center bg-white rounded-lg border border-gray-200 shadow-sm">
            <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="mt-4 text-lg font-medium text-gray-900">No milestone data available for this project</h3>
            <p className="mt-2 text-sm text-gray-600">
              {pid ? 'No milestones found for the selected project and state filter.' : 'Please select a project to view milestone information.'}
            </p>
          </div>
        )
      )}
    </div>
  );
};

export default ProjectMilestone;